import { Dayjs } from "dayjs";
import RepositoryBase from "./RepositoryBase";
import { ResponseEnvelope } from "./RepositoryBase";
import WorkerGetAllTipsResponse from "./WorkerResponses/WorkerGetAllTipsResponse";
import WorkerGetTipResponse from "./WorkerResponses/WorkerGetTipResponse";
import WorkerGetAllTipStatussesResponse from "./WorkerResponses/WorkerGetAllTipStatussesResponse";
import WorkerTipHistoryResponse from "./WorkerResponses/WorkerTipHistoryResponse";
import WorkerGetAllTipHistoryResponse from "./WorkerResponses/WorkerGetAllTipHistoryResponse";
import { TipStatus } from "../Types/Enums/TipStatus";
import WorkerGetAllResponse from "./WorkerResponses/WorkerGetAllResponse";
import WorkerGetResponse from "./WorkerResponses/WorkerGetResponse";
import WorkerData from "../Types/WorkerData";
import WorkerCreateResponse from "./WorkerResponses/WorkerCreateResponse";
import WorkerGetCountPeriodTipHistoryResponse from "./WorkerResponses/WorkerGetCountPeriodTipHistoryResponse";
import WorkerCountPeriodTipsResponse from "./WorkerResponses/WorkerCountPeriodTipsResponse";
import WorkerGetWorkedDaysResponse from "./WorkerResponses/WorkerGetWorkedDaysResponse";
import WorkerWorkedDayResponse from "./WorkerResponses/WorkerWorkedDayResponse";
import WorkerWorkedPeriodResponse from "./WorkerResponses/WorkerWorkedPeriodResponse";
import WorkerMergeResponse from "./WorkerResponses/WorkerMergeResponse";

export default class WorkerRepository extends RepositoryBase {
    public static async getWorkersTips(locationId: string, startDate: Dayjs, endDate: Dayjs): Promise<ResponseEnvelope<WorkerGetAllTipsResponse>> {
        try {
            const csrf = await this.getCsrf();
            if (csrf.isFailed()) return new ResponseEnvelope<WorkerGetAllTipsResponse>(false, undefined, csrf.errorTitle, csrf.errorMessage);
            const token = csrf.result!;

            var response = await fetch(`${this.protocol}://${this.ip}:${this.port}/api/${locationId}/workday/workers/tips`, {
                method: 'POST',
                mode: "cors",
                credentials: 'include',
                headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': token },
                body: JSON.stringify({
                    startDate: startDate.format("YYYY-MM-DD"),
                    endDate: endDate.format("YYYY-MM-DD"),
                })
            });

            if (!response.ok) return this.invalidResponse();

            var data = await response.json();
            if (!data.success) return this.requestUnsuccessful();

            return new ResponseEnvelope<WorkerGetAllTipsResponse>(
                true,
                {
                    workers: data.response.map((x: any) => ({
                        id: x.workerId,
                        firstName: x.firstName,
                        lastName: x.lastName,
                        cashTip: x.cashTip,
                        pinTip: x.pinTip,
                        debt: x.debt,
                        roundedTip: x.roundedTip,
                    } as WorkerGetTipResponse))
                }
            );
        } catch (err) {
            if (err instanceof SyntaxError) {
                return this.invalidFormat();
            } else if (err instanceof TypeError) {
                return this.unableToSendRequest();
            } else {
                console.log(err);
                return this.unexpectedError();
            }
        }
    }

    public static async getWorkerTipStatuses(locationId: string, month: Dayjs): Promise<ResponseEnvelope<WorkerGetAllTipStatussesResponse>> {
        try {
            const csrf = await this.getCsrf();
            if (csrf.isFailed()) return new ResponseEnvelope<WorkerGetAllTipStatussesResponse>(false, undefined, csrf.errorTitle, csrf.errorMessage);
            const token = csrf.result!;

            var response = await fetch(`${this.protocol}://${this.ip}:${this.port}/api/${locationId}/worker/getall/tipstatus`, {
                method: 'POST',
                mode: "cors",
                credentials: 'include',
                headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': token },
                body: JSON.stringify({
                    month: month.format("YYYY-MM-DD"),
                })
            });

            if (!response.ok) return this.invalidResponse();

            var data = await response.json();
            if (!data.success) return this.requestUnsuccessful();

            return new ResponseEnvelope<WorkerGetAllTipStatussesResponse>(
                true,
                {
                    statusses: data.response,
                } as WorkerGetAllTipStatussesResponse
            );
        } catch (err) {
            if (err instanceof SyntaxError) {
                return this.invalidFormat();
            } else if (err instanceof TypeError) {
                return this.unableToSendRequest();
            } else {
                console.log(err);
                return this.unexpectedError();
            }
        }
    }

    public static async setWorkerTipStatus(locationId: string, workerId: string, status: Record<string, TipStatus>): Promise<ResponseEnvelope<null>> {
        try {
            const csrf = await this.getCsrf();
            if (csrf.isFailed()) return new ResponseEnvelope<null>(false, undefined, csrf.errorTitle, csrf.errorMessage);
            const token = csrf.result!;

            var response = await this.sendWithCsrf(`${this.protocol}://${this.ip}:${this.port}/api/${locationId}/worker/tipstatus/${workerId}`, {
                method: 'PUT',
                mode: "cors",
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    status: status
                })
            }, token);

            if (!response.ok) return this.invalidResponse();

            var data = await response.json();
            if (!data.success) return this.requestUnsuccessful();

            return new ResponseEnvelope<null>(
                true,
                null
            );
        } catch (err) {
            if (err instanceof SyntaxError) {
                return this.invalidFormat();
            } else if (err instanceof TypeError) {
                return this.unableToSendRequest();
            } else {
                console.log(err);
                return this.unexpectedError();
            }
        }
    }

    public static async setWorkerDebt(locationId: string, workerId: string, debt: number): Promise<ResponseEnvelope<null>> {
        try {
            const csrf = await this.getCsrf();
            if (csrf.isFailed()) return new ResponseEnvelope<null>(false, undefined, csrf.errorTitle, csrf.errorMessage);
            const token = csrf.result!;

            var response = await this.sendWithCsrf(`${this.protocol}://${this.ip}:${this.port}/api/${locationId}/worker/${workerId}/debt`, {
                method: 'PUT',
                mode: "cors",
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    debt: debt,
                })
            }, token);

            if (!response.ok) return this.invalidResponse();

            var data = await response.json();
            if (!data.success) return this.requestUnsuccessful();

            return new ResponseEnvelope<null>(
                true,
                null
            );
        } catch (err) {
            if (err instanceof SyntaxError) {
                return this.invalidFormat();
            } else if (err instanceof TypeError) {
                return this.unableToSendRequest();
            } else {
                console.log(err);
                return this.unexpectedError();
            }
        }
    }

    public static async getWorkerTipHistory(locationId: string, startMonth: Dayjs, endMonth: Dayjs, workers: string[]): Promise<ResponseEnvelope<WorkerGetAllTipHistoryResponse>> {
        try {
            const csrf = await this.getCsrf();
            if (csrf.isFailed()) return new ResponseEnvelope<WorkerGetAllTipHistoryResponse>(false, undefined, csrf.errorTitle, csrf.errorMessage);
            const token = csrf.result!;

            var response = await fetch(`${this.protocol}://${this.ip}:${this.port}/api/${locationId}/worker/getall/tiphistory`, {
                method: 'POST',
                mode: "cors",
                credentials: 'include',
                headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': token },
                body: JSON.stringify({
                    startMonth: startMonth.format("YYYY-MM-DD"),
                    endMonth: endMonth.format("YYYY-MM-DD"),
                    workers: workers,

                })
            });

            if (!response.ok) return this.invalidResponse();

            var data = await response.json();
            if (!data.success) return this.requestUnsuccessful();

            return new ResponseEnvelope<WorkerGetAllTipHistoryResponse>(
                true,
                {
                    response: data.response.map((x: any) => ({
                        id: x.id,
                        cashTip: x.cashTip,
                        pinTip: x.pinTip,
                        status: x.status
                    } as WorkerTipHistoryResponse)),
                } as WorkerGetAllTipHistoryResponse
            );
        } catch (err) {
            if (err instanceof SyntaxError) {
                return this.invalidFormat();
            } else if (err instanceof TypeError) {
                return this.unableToSendRequest();
            } else {
                console.log(err);
                return this.unexpectedError();
            }
        }
    }

    public static async getAllWorkers(locationId: string): Promise<ResponseEnvelope<WorkerGetAllResponse>> {
        try {
            const csrf = await this.getCsrf();
            if (csrf.isFailed()) return new ResponseEnvelope<WorkerGetAllResponse>(false, undefined, csrf.errorTitle, csrf.errorMessage);
            const token = csrf.result!;

            var response = await fetch(`${this.protocol}://${this.ip}:${this.port}/api/${locationId}/worker/getall`, {
                method: 'GET',
                mode: "cors",
                credentials: 'include',
                headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': token },
            });

            if (!response.ok) return this.invalidResponse();

            var data = await response.json();
            if (!data.success) return this.requestUnsuccessful();

            return new ResponseEnvelope<WorkerGetAllResponse>(
                true,
                {
                    response: data.response.map((x: any) => ({
                        id: x.id,
                        userId: x.userId,
                        firstName: x.firstName,
                        lastName: x.lastName,
                        debt: x.debt,
                        permissions: x.permission,
                        function: x.function,
                    } as WorkerGetResponse)),
                } as WorkerGetAllResponse
            );
        } catch (err) {
            if (err instanceof SyntaxError) {
                return this.invalidFormat();
            } else if (err instanceof TypeError) {
                return this.unableToSendRequest();
            } else {
                console.log(err);
                return this.unexpectedError();
            }
        }
    }

    public static async getWorkerById(locationId: string, workerId: string): Promise<ResponseEnvelope<WorkerGetResponse>> {
        try {
            const csrf = await this.getCsrf();
            if (csrf.isFailed()) return new ResponseEnvelope<WorkerGetResponse>(false, undefined, csrf.errorTitle, csrf.errorMessage);
            const token = csrf.result!;

            var response = await fetch(`${this.protocol}://${this.ip}:${this.port}/api/${locationId}/worker/${workerId}`, {
                method: 'GET',
                mode: "cors",
                credentials: 'include',
                headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': token },
            });

            if (!response.ok) return this.invalidResponse();

            var data = await response.json();
            if (!data.success) return this.requestUnsuccessful();

            return new ResponseEnvelope<WorkerGetResponse>(
                true,
                {
                    id: data.response.id,
                    userId: data.response.userId,
                    firstName: data.response.firstName,
                    lastName: data.response.lastName,
                    debt: data.response.debt,
                    permissions: data.response.permission,
                    function: data.response.function,
                } as WorkerGetResponse
            );
        } catch (err) {
            if (err instanceof SyntaxError) {
                return this.invalidFormat();
            } else if (err instanceof TypeError) {
                return this.unableToSendRequest();
            } else {
                console.log(err);
                return this.unexpectedError();
            }
        }
    }

    public static async getWorkerCountPeriodTipHistory(locationId: string, workerId: string, startDate: Dayjs, endDate: Dayjs): Promise<ResponseEnvelope<WorkerGetCountPeriodTipHistoryResponse>> {
        try {
            const csrf = await this.getCsrf();
            if (csrf.isFailed()) return new ResponseEnvelope<WorkerGetCountPeriodTipHistoryResponse>(false, undefined, csrf.errorTitle, csrf.errorMessage);
            const token = csrf.result!;

            const params = new URLSearchParams({
                startDate: startDate.format("YYYY-MM-DD"),
                endDate: endDate.format("YYYY-MM-DD"),
            });

            var response = await fetch(`${this.protocol}://${this.ip}:${this.port}/api/${locationId}/worker/${workerId}/tiphistory?${params.toString()}`, {
                method: 'GET',
                mode: "cors",
                credentials: 'include',
                headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': token },
            });

            if (!response.ok) return this.invalidResponse();

            var data = await response.json();
            if (!data.success) return this.requestUnsuccessful();

            return new ResponseEnvelope<WorkerGetCountPeriodTipHistoryResponse>(
                true,
                {
                    cashCountPeriods: data.cashCountPeriods.map((x: any) => ({
                        countPeriodId: x.countPeriodId,
                        countPeriodStartDate: x.countPeriodStartDate,
                        countPeriodEndDate: x.countPeriodEndDate,
                        targetStartDate: x.targetStartDate,
                        targetEndDate: x.targetEndDate,
                        cashTip: x.cashTip,
                        pinTip: x.pinTip,
                    } as WorkerCountPeriodTipsResponse)),
                    pinCountPeriods: data.pinCountPeriods.map((x: any) => ({
                        countPeriodId: x.countPeriodId,
                        countPeriodStartDate: x.countPeriodStartDate,
                        countPeriodEndDate: x.countPeriodEndDate,
                        targetStartDate: x.targetStartDate,
                        targetEndDate: x.targetEndDate,
                        cashTip: x.cashTip,
                        pinTip: x.pinTip,
                    } as WorkerCountPeriodTipsResponse)),
                }
            );
        } catch (err) {
            if (err instanceof SyntaxError) {
                return this.invalidFormat();
            } else if (err instanceof TypeError) {
                return this.unableToSendRequest();
            } else {
                console.log(err);
                return this.unexpectedError();
            }
        }
    }

    public static async getWorkerWorkedDays(locationId: string, workerId: string, startDate: Dayjs, endDate: Dayjs): Promise<ResponseEnvelope<WorkerGetWorkedDaysResponse>> {
        try {
            const csrf = await this.getCsrf();
            if (csrf.isFailed()) return new ResponseEnvelope<WorkerGetWorkedDaysResponse>(false, undefined, csrf.errorTitle, csrf.errorMessage);
            const token = csrf.result!;

            const params = new URLSearchParams({
                startDate: startDate.format("YYYY-MM-DD"),
                endDate: endDate.format("YYYY-MM-DD"),
            });

            var response = await fetch(`${this.protocol}://${this.ip}:${this.port}/api/${locationId}/worker/${workerId}/workperiods?${params.toString()}`, {
                method: 'GET',
                mode: "cors",
                credentials: 'include',
                headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': token },
            });

            if (!response.ok) return this.invalidResponse();

            var data = await response.json();
            if (!data.success) return this.requestUnsuccessful();

            return new ResponseEnvelope<WorkerGetWorkedDaysResponse>(
                true,
                {
                    response: data.response.map((x: any) => ({
                        id: x.id,
                        date: x.date,
                        workPeriods: x.workPeriods.map((period: any) => ({
                            id: period.id,
                            name: period.name,
                            date: period.date,
                            cashTip: period.cashTip,
                            pinTip: period.pinTip,
                        } as WorkerWorkedPeriodResponse)),
                    } as WorkerWorkedDayResponse)),
                }
            );
        } catch (err) {
            if (err instanceof SyntaxError) {
                return this.invalidFormat();
            } else if (err instanceof TypeError) {
                return this.unableToSendRequest();
            } else {
                console.log(err);
                return this.unexpectedError();
            }
        }
    }

    public static async createWorker(locationId: string, worker: WorkerData): Promise<ResponseEnvelope<WorkerCreateResponse>> {
        try {
            const csrf = await this.getCsrf();
            if (csrf.isFailed()) return new ResponseEnvelope<WorkerCreateResponse>(false, undefined, csrf.errorTitle, csrf.errorMessage);
            const token = csrf.result!;

            var response = await this.sendWithCsrf(`${this.protocol}://${this.ip}:${this.port}/api/${locationId}/worker/create`, {
                method: 'POST',
                mode: "cors",
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    firstname: worker.FirstName,
                    lastname: worker.LastName,
                    permission: worker.Permission,
                    function: worker.Function,
                })
            }, token);

            if (!response.ok) return this.invalidResponse();

            var data = await response.json();
            if (!data.success) return this.requestUnsuccessful();

            return new ResponseEnvelope<WorkerCreateResponse>(
                true,
                {
                    id: data.id
                } as WorkerCreateResponse
            );
        } catch (err) {
            if (err instanceof SyntaxError) {
                return this.invalidFormat();
            } else if (err instanceof TypeError) {
                return this.unableToSendRequest();
            } else {
                console.log(err);
                return this.unexpectedError();
            }
        }
    }

    public static async updateWorker(locationId: string, workerId: string, worker: Pick<WorkerData, "FirstName" | "LastName" | "Function">): Promise<ResponseEnvelope<null>> {
        try {
            const csrf = await this.getCsrf();
            if (csrf.isFailed()) return new ResponseEnvelope<null>(false, undefined, csrf.errorTitle, csrf.errorMessage);
            const token = csrf.result!;

            var response = await this.sendWithCsrf(`${this.protocol}://${this.ip}:${this.port}/api/${locationId}/worker/${workerId}`, {
                method: 'PUT',
                mode: "cors",
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    firstname: worker.FirstName,
                    lastname: worker.LastName,
                    function: worker.Function,
                })
            }, token);

            if (!response.ok) return this.invalidResponse();

            var data = await response.json();
            if (!data.success) return this.requestUnsuccessful();

            return new ResponseEnvelope<null>(
                true,
                null
            );
        } catch (err) {
            if (err instanceof SyntaxError) {
                return this.invalidFormat();
            } else if (err instanceof TypeError) {
                return this.unableToSendRequest();
            } else {
                console.log(err);
                return this.unexpectedError();
            }
        }
    }

    public static async deleteWorker(locationId: string, workerId: string): Promise<ResponseEnvelope<null>> {
        try {
            const csrf = await this.getCsrf();
            if (csrf.isFailed()) return new ResponseEnvelope<null>(false, undefined, csrf.errorTitle, csrf.errorMessage);
            const token = csrf.result!;

            var response = await this.sendWithCsrf(`${this.protocol}://${this.ip}:${this.port}/api/${locationId}/worker/${workerId}`, {
                method: 'DELETE',
                mode: "cors",
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
            }, token);

            if (!response.ok) return this.invalidResponse();

            var data = await response.json();
            if (!data.success) return this.requestUnsuccessful();

            return new ResponseEnvelope<null>(
                true,
                null
            );
        } catch (err) {
            if (err instanceof SyntaxError) {
                return this.invalidFormat();
            } else if (err instanceof TypeError) {
                return this.unableToSendRequest();
            } else {
                console.log(err);
                return this.unexpectedError();
            }
        }
    }

    public static async mergeWorkers(locationId: string, workerId: string, mergeIntoWorkerId: string): Promise<ResponseEnvelope<WorkerMergeResponse>> {
        try {
            const csrf = await this.getCsrf();
            if (csrf.isFailed()) return new ResponseEnvelope<WorkerMergeResponse>(false, undefined, csrf.errorTitle, csrf.errorMessage);
            const token = csrf.result!;

            var response = await this.sendWithCsrf(`${this.protocol}://${this.ip}:${this.port}/api/${locationId}/worker/merge`, {
                method: 'POST',
                mode: "cors",
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    workerId: workerId,
                    mergeIntoWorkerId: mergeIntoWorkerId,
                })
            }, token);

            if (!response.ok) return this.invalidResponse();

            var data = await response.json();
            if (!data.success) return this.requestUnsuccessful();

            return new ResponseEnvelope<WorkerMergeResponse>(
                true,
                {
                    mergedIntoWorkerId: data.mergedIntoWorkerId,
                }
            );
        } catch (err) {
            if (err instanceof SyntaxError) {
                return this.invalidFormat();
            } else if (err instanceof TypeError) {
                return this.unableToSendRequest();
            } else {
                console.log(err);
                return this.unexpectedError();
            }
        }
    }
}
